import { useTranslation } from 'react-i18next'
import {
  CircleCheck,
  CircleCheckBig,
  CirclePause,
  CircleStop,
  Bookmark,
  PencilLine,
  Send,
  type LucideIcon,
} from 'lucide-react'
import { DeliveryStatusBadge, type StatusPillSize } from './DeliveryStatusBadge'
import { StatusSelect } from './StatusSelect'

/**
 * The user-set state of a line item — what the user says should happen, as
 * opposed to the system-set `executionState` that says what is happening.
 *
 * The API imposes no transition rules, so any state can follow any other.
 */
export type UserState =
  | 'PROPOSED'
  | 'SUBMITTED'
  | 'RESERVED'
  | 'APPROVED'
  | 'PAUSED'
  | 'STOPPED'
  | 'COMPLETED'

/** Every user state, in workflow order: proposed → approved → paused/stopped. */
export const USER_STATES: UserState[] = [
  'PROPOSED',
  'SUBMITTED',
  'RESERVED',
  'APPROVED',
  'PAUSED',
  'STOPPED',
  'COMPLETED',
]

/**
 * The states a user can pick. COMPLETED is left out — only the ad server sets
 * it, once delivery is done.
 */
export const SELECTABLE_USER_STATES: UserState[] = USER_STATES.filter((s) => s !== 'COMPLETED')

/** A marketplace line item is only ever running, held or ended. */
export const MARKETPLACE_USER_STATES: UserState[] = ['APPROVED', 'PAUSED', 'STOPPED']

export const USER_STATE_ICONS: Record<UserState, LucideIcon> = {
  PROPOSED: PencilLine,
  SUBMITTED: Send,
  RESERVED: Bookmark,
  APPROVED: CircleCheck,
  PAUSED: CirclePause,
  STOPPED: CircleStop,
  COMPLETED: CircleCheckBig,
}

/** A TanStack Form field-like object, so call sites can pass a field directly. */
export interface UserStateField {
  state: { value: string }
  handleChange: (value: string) => void
}

export interface UserStateSelectProps {
  value?: string
  onChange?: (state: UserState) => void
  /** Pass a TanStack Form field instead of value/onChange — takes precedence. */
  field?: UserStateField
  /** Narrow the states offered — defaults to every user-selectable one. */
  states?: UserState[]
  size?: StatusPillSize
  /** Shows the badge on its own, without the menu. */
  readOnly?: boolean
}

/**
 * Badge that doubles as a state picker: shows the current user state and, when
 * it can be changed, opens a menu of the states that can be picked instead.
 */
export function UserStateSelect({
  value: valueProp,
  onChange,
  field,
  states = SELECTABLE_USER_STATES,
  readOnly = false,
  size,
}: UserStateSelectProps) {
  const { t } = useTranslation()

  const value = field ? field.state.value : (valueProp ?? '')
  const handleChange = field ? (s: UserState) => field.handleChange(s) : onChange

  return (
    <StatusSelect
      value={value}
      onChange={readOnly ? undefined : handleChange}
      states={states}
      size={size}
      describe={(state) => t(`userStates.descriptions.${state}`)}
      renderBadge={(state, trailing) => (
        <DeliveryStatusBadge
          status={state}
          size={size}
          icon={USER_STATE_ICONS[state as UserState]}
          trailing={trailing}
        />
      )}
    />
  )
}
